import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, UserPlus } from 'lucide-react';
import ForgotPasswordDialog from '@/components/ForgotPasswordDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

export default function AdminLogin() {
  const navigate = useNavigate();
  const { token, login } = useAuth();
  const [isSetup, setIsSetup] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ username: '', password: '' });
  const [submitting, setSubmitting] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  useEffect(() => {
    if (token) { navigate('/admin', { replace: true }); return; }
    api.get('/admin/check').then(r => { setIsSetup(!r.data.admin_exists); setLoading(false); }).catch(() => setLoading(false));
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.username || !form.password) { toast.error("Remplissez tous les champs"); return; }
    setSubmitting(true);
    try {
      const endpoint = isSetup ? '/admin/setup' : '/admin/login';
      const res = await api.post(endpoint, form);
      login(res.data.token, { username: res.data.username });
      toast.success(isSetup ? "Compte administrateur créé !" : "Connecté !");
      navigate('/admin', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de connexion");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="animate-spin h-8 w-8 border-4 border-violet-600 border-t-transparent rounded-full" /></div>;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src="/logo-header.png" alt="Plume et Pion" className="h-32 w-auto mx-auto" data-testid="login-logo-img" />
          <p className="text-sm text-slate-500 mt-4 font-medium uppercase tracking-widest">{isSetup ? 'Créer le compte administrateur' : 'Espace administration'}</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid="admin-login-form">
          <div className="space-y-4">
            <div>
              <Label htmlFor="username" className="text-xs font-medium text-slate-500">Nom d'utilisateur</Label>
              <Input id="username" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} className="mt-1 rounded-lg" autoFocus data-testid="admin-username-input" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-medium text-slate-500">Mot de passe</Label>
              <Input id="password" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} className="mt-1 rounded-lg" data-testid="admin-password-input" />
            </div>
          </div>
          <Button type="submit" className="w-full mt-6 rounded-full" disabled={submitting} data-testid="admin-login-btn">
            {submitting ? '...' : isSetup ? <><UserPlus className="h-4 w-4 mr-2" />Créer le compte</> : <><LogIn className="h-4 w-4 mr-2" />Se connecter</>}
          </Button>
          {!isSetup && (
            <button type="button" onClick={() => setForgotOpen(true)} className="w-full mt-4 text-sm text-slate-500 hover:text-violet-600 transition-colors" data-testid="forgot-password-link">
              Mot de passe oublié ?
            </button>
          )}
        </form>
        <ForgotPasswordDialog open={forgotOpen} onClose={() => setForgotOpen(false)} />
      </div>
    </div>
  );
}
